import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { Button, cn, GhostButton, Label, Slider } from '@crabs-ai/ds-react';
import {
  PauseFill,
  PlayArrowFill,
  Replay,
  Settings,
} from '@crabs-ai/web-icons';
import TtsDialog from '@/shared/components/dialog/TtsDialog/TtsDialog';

import { useLocale, useTranslations } from 'next-intl';
import { TTSVoice } from '@/shared/components/dialog/TtsDialog/TtsDialogContext';
import { formatTtsDisplayName } from '@/shared/lib/format-tts-display-name';
import { PlayerRef } from '@remotion/player';
import { useIsPlaying } from '@/features/remotion-editor/editor/playback-controls/use-is-playing';
import { isHighQualityTtsBlockedOnPlan } from '@/shared/user-plan/user-spec';
import { useGetSpaceMeApi } from '@/features/my-space/api/get-space-me';

export interface TtsTextCardProps {
  cardId: string;
  label: string;
  timelineLabel?: string;
  isActive: boolean;
  isTextDisabled: boolean;
  isLocked?: boolean;
  audioSrc?: string | null;
  audioPreview?: React.ReactNode;
  selectedVoice: TTSVoice | null;
  onVoiceChange: (voice: TTSVoice) => void;
  textContentRef: React.RefObject<HTMLParagraphElement | null>;
  onTextPointerDown?: () => void;
  onTextFocus?: () => void;
  onTextBlur?: (text: string) => void;
  onTextInput?: (text: string) => void;
  cardBorderClass: string;
  isDirty: boolean;
  isVoiceDirty: boolean;
  isPending: boolean;
  onAction: () => void;
  onResetVoiceSettings?: () => void;
  actionLabel: string;
  actionDisabled?: boolean;
  playerRef: React.RefObject<PlayerRef | null>;
  placeholder?: string;
}

function TtsTextCard({
  cardId,
  label,
  timelineLabel,
  isActive,
  isTextDisabled,
  isLocked = false,
  audioSrc,
  audioPreview,
  selectedVoice,
  onVoiceChange,
  textContentRef,
  onTextPointerDown,
  onTextFocus,
  onTextBlur,
  onTextInput,
  cardBorderClass,
  isDirty,
  isVoiceDirty,
  isPending,
  onAction,
  onResetVoiceSettings,
  actionLabel,
  actionDisabled = false,
  playerRef,
  placeholder,
}: TtsTextCardProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { data: spaceData } = useGetSpaceMeApi();
  const isEditorPlaying = useIsPlaying(playerRef);

  const textLabelId = useId();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isTtsDialogOpen, setIsTtsDialogOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const isSeeking = useRef(false);

  const canPlayAudio = Boolean(audioSrc);
  const selectedVoiceSignature = useMemo(() => {
    if (!selectedVoice) return 'none';
    return `${selectedVoice.voice.code}:${selectedVoice.tempo ?? ''}:${selectedVoice.pitch ?? ''}:${selectedVoice.emotion?.code ?? ''}:${selectedVoice.style ?? ''}`;
  }, [selectedVoice]);

  React.useEffect(() => {
    if (!audioRef.current) return;

    if (isEditorPlaying) {
      setIsPlaying(false);
      audioRef.current.pause();
      isSeeking.current = false;
      return;
    }

    if (isPlaying) {
      playerRef?.current?.pause?.();
    }
  }, [isEditorPlaying, isPlaying, playerRef]);

  React.useEffect(() => {
    if (!isActive) {
      audioRef.current?.pause();
      isSeeking.current = false;
    }
  }, [isActive]);

  React.useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setAudioProgress(0);
    isSeeking.current = false;
  }, [audioSrc]);

  React.useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setAudioProgress(0);
    isSeeking.current = false;
  }, [selectedVoiceSignature]);

  const handleTogglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    if (isEditorPlaying) {
      playerRef.current?.pause();
    }

    audio.play().catch(() => setIsPlaying(false));
  }, [isPlaying, isEditorPlaying, playerRef]);

  const handleAudioPlay = useCallback(() => setIsPlaying(true), []);
  const handleAudioPause = useCallback(() => setIsPlaying(false), []);
  const handleAudioEnded = useCallback(() => {
    if (audioRef.current) audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setAudioProgress(0);
  }, []);

  const handleAudioTimeUpdate = useCallback(() => {
    if (isSeeking.current) return;
    const audio = audioRef.current;
    if (!audio || !audio.duration || Number.isNaN(audio.duration)) return;
    setAudioProgress((audio.currentTime / audio.duration) * 100);
  }, []);

  const handleProgressChange = useCallback((values: number[]) => {
    isSeeking.current = true;
    setAudioProgress(values[0] ?? 0);
  }, []);

  const handleProgressCommit = useCallback((values: number[]) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration || Number.isNaN(audio.duration)) {
      isSeeking.current = false;
      return;
    }
    const percent = values[0] ?? 0;
    audio.currentTime = (percent / 100) * audio.duration;
    setAudioProgress(percent);
    isSeeking.current = false;
  }, []);

  const playbackLabel = useMemo(
    () =>
      isPlaying
        ? t('Editor.script-panel.pause-audio')
        : t('Editor.script-panel.play-audio'),
    [isPlaying, t],
  );

  const handleTextKeyEvent = useCallback(
    (event: React.KeyboardEvent<HTMLParagraphElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const handleTextClipboardEvent = useCallback(
    (event: React.ClipboardEvent<HTMLParagraphElement>) => {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation?.();
    },
    [],
  );

  const isPlanBlockingHighQuality = useMemo(() => {
    if (!spaceData?.data?.planId) return false;
    return isHighQualityTtsBlockedOnPlan(spaceData?.data?.planId);
  }, [spaceData?.data?.planId]);
  const showPlayButton = !isDirty;
  const showResetButton = isDirty && isVoiceDirty;

  return (
    <div
      className={cn('flex flex-col gap-2xs overflow-hidden rounded-2xs')}
      data-script-card-id={cardId}
      data-active-script-card={isActive ? 'true' : undefined}
    >
      <div
        className={cn(
          'flex flex-col gap-2xs px-xs py-2xs transition-colors duration-200 ease-out',
          cardBorderClass,
        )}
      >
        <Label
          color={'neutral'}
          size={'medium'}
        >
          {label}
        </Label>
        <div className={'flex flex-col gap-4xs'}>
          {timelineLabel && (
            <span className="text-title-small text-sys-cont-neutral-default">
              {`[${timelineLabel}]`}
            </span>
          )}
          <label
            id={textLabelId}
            className="sr-only"
          >
            {t('Editor.script-panel.textarea-label')}
          </label>
          <p
            ref={textContentRef}
            className={cn(
              'no-scrollbar relative w-full rounded-xs bg-transparent text-sys-cont-inverse-dark outline-none',
              'empty:before:pointer-events-none empty:before:text-body-medium empty:before:text-sys-cont-neutral-light empty:before:content-[attr(data-placeholder)]',
              isTextDisabled && !isLocked && 'cursor-wait opacity-60',
              isLocked && 'cursor-default',
            )}
            role="textbox"
            aria-multiline="true"
            aria-labelledby={textLabelId}
            aria-disabled={isTextDisabled}
            aria-readonly={isTextDisabled}
            data-disabled={isTextDisabled ? 'true' : undefined}
            data-placeholder={
              placeholder ?? t('Editor.script-panel.textarea-placeholder')
            }
            tabIndex={isTextDisabled ? -1 : 0}
            contentEditable={!isTextDisabled && !isLocked}
            suppressContentEditableWarning
            style={{ whiteSpace: 'pre-wrap' }}
            onPointerDown={() => onTextPointerDown?.()}
            onFocus={(event) => {
              if (isTextDisabled) {
                event.preventDefault();
                return;
              }
              onTextFocus?.();
            }}
            onBlur={(event) => {
              if (isTextDisabled) {
                event.preventDefault();
                return;
              }
              onTextBlur?.(event.currentTarget.textContent ?? '');
            }}
            onInput={(event) => {
              if (isTextDisabled) {
                event.preventDefault();
                return;
              }
              event.stopPropagation();
              onTextInput?.(event.currentTarget.textContent ?? '');
            }}
            onKeyDown={(event) => {
              if (isTextDisabled) {
                event.preventDefault();
                return;
              }
              handleTextKeyEvent(event);
            }}
            onKeyUp={(event) => {
              if (isTextDisabled) {
                event.preventDefault();
                return;
              }
              handleTextKeyEvent(event);
            }}
            onCopy={handleTextClipboardEvent}
            onCut={handleTextClipboardEvent}
            onPaste={handleTextClipboardEvent}
          />
        </div>
        {isActive && (
          <div className="flex w-full flex-col gap-2xs transition-all duration-200 ease-out">
            <div className="flex items-center justify-between gap-2xs">
              <button
                aria-label={t('Editor.script-panel.settings')}
                className={cn(
                  'w-full cursor-pointer rounded-2xs px-xs py-3xs transition-all duration-200 ease-out outline-none',
                  'bg-sys-style-neutral-lightest data-[disabled=false]:hover:bg-sys-style-neutral-light data-[disabled=false]:active:bg-sys-style-neutral-default',
                  isTextDisabled && 'cursor-wait opacity-40',
                  isLocked && 'pointer-events-none cursor-default opacity-40',
                )}
                disabled={isTextDisabled}
                data-disabled={isTextDisabled ? 'true' : 'false'}
                onClick={() => setIsTtsDialogOpen(true)}
              >
                <div className={'flex items-center justify-between gap-4xs'}>
                  <span className="flex-1 text-start text-comp-moderate-medium text-sys-cont-neutral-default">
                    {formatTtsDisplayName(selectedVoice, locale, audioPreview ? '음성 설정 연결 전' : 'None')}
                  </span>
                  <Settings
                    className={'size-4 [&_path]:fill-sys-cont-neutral-default'}
                  />
                </div>
              </button>
              <div className="inline-grid min-w-fit place-items-center">
                {audioPreview ? (showPlayButton ? audioPreview : null) : <GhostButton
                  className={cn(
                    'col-start-1 row-start-1 flex min-w-fit items-center justify-center transition-all duration-200 ease-out [&_path]:fill-sys-cont-neutral-default',
                    showPlayButton
                      ? canPlayAudio
                        ? 'scale-100 opacity-100'
                        : 'scale-100 opacity-40'
                      : 'pointer-events-none scale-95 opacity-0',
                  )}
                  leadingNode={
                    isPlaying ? (
                      <PauseFill className="size-6" />
                    ) : (
                      <PlayArrowFill className="size-6" />
                    )
                  }
                  onClick={handleTogglePlayback}
                  aria-label={playbackLabel}
                  disabled={!canPlayAudio || isPending}
                  state={isPending ? 'disabled' : 'default'}
                >
                  {isPlaying
                    ? t('Editor.script-panel.stop-audio-button-label')
                    : t('Editor.script-panel.play-audio-button-label')}
                </GhostButton>}
                <GhostButton
                  className={cn(
                    'col-start-1 row-start-1 flex min-w-fit items-center justify-center text-sys-cont-neutral-light transition-all duration-200 ease-out [&_path]:fill-sys-cont-neutral-light',
                    showResetButton
                      ? 'scale-100 opacity-100'
                      : 'pointer-events-none scale-95 opacity-0',
                    (isTextDisabled || isLocked) && 'opacity-40',
                  )}
                  leadingNode={<Replay className="size-5" />}
                  onClick={() => onResetVoiceSettings?.()}
                  disabled={!showResetButton || isTextDisabled || isLocked}
                  state={isTextDisabled || isLocked ? 'disabled' : 'default'}
                  hierarchy="tertiary"
                >
                  {t('Common.reset')}
                </GhostButton>
              </div>
            </div>
            {!audioPreview && canPlayAudio && !isDirty && (
              <Slider
                value={[audioProgress]}
                onValueChange={handleProgressChange}
                onValueCommit={handleProgressCommit}
                min={0}
                max={100}
                step={0.5}
                className="w-full"
                isShowTooltip={false}
                aria-label="Audio seek"
              />
            )}
            <div
              className={cn(
                'overflow-hidden transition-all duration-200 ease-out',
                isDirty
                  ? 'max-h-10 translate-y-0 opacity-100'
                  : 'pointer-events-none max-h-0 -translate-y-1 opacity-0',
              )}
            >
              <Button
                className="w-full"
                hierarchy={'primary-variant'}
                size={'small'}
                state={isPending ? 'loading' : 'default'}
                onClick={onAction}
                disabled={!isDirty || actionDisabled}
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        )}
        {audioSrc && (
          <audio
            ref={audioRef}
            src={audioSrc}
            preload="auto"
            onPlay={handleAudioPlay}
            onPause={handleAudioPause}
            onEnded={handleAudioEnded}
            onTimeUpdate={handleAudioTimeUpdate}
          />
        )}

        {isTtsDialogOpen && (
          <TtsDialog
            open={isTtsDialogOpen}
            showPlanWarning={isPlanBlockingHighQuality}
            planId={spaceData?.data?.planId ?? undefined}
            setOpen={() => setIsTtsDialogOpen(false)}
            onConfirm={onVoiceChange}
            defaultTts={selectedVoice}
          />
        )}
      </div>
    </div>
  );
}

export default TtsTextCard;
